import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  type Company,
  type CompanyInput,
  LEGAL_FORM_LABELS,
  LEGAL_FORM_ICONS,
} from "@/hooks/useCompanies";
import type { Database } from "@/integrations/supabase/types";

type CompanyLegalForm = Database["public"]["Enums"]["company_legal_form"];

interface CompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSubmit: (data: CompanyInput) => Promise<void>;
  isLoading?: boolean;
}

const LEGAL_FORMS: CompanyLegalForm[] = [
  "auto_entrepreneur",
  "eurl",
  "sarl",
  "sas",
  "sasu",
  "sci",
  "association",
  "other",
];

export function CompanyDialog({
  open,
  onOpenChange,
  company,
  onSubmit,
  isLoading,
}: CompanyDialogProps) {
  const [name, setName] = useState("");
  const [legalForm, setLegalForm] = useState<CompanyLegalForm>("auto_entrepreneur");
  const [siren, setSiren] = useState("");
  const [siret, setSiret] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [activityDescription, setActivityDescription] = useState("");

  const isEditing = !!company;

  // Reset form when dialog opens/closes or company changes
  useEffect(() => {
    if (open) {
      if (company) {
        setName(company.name);
        setLegalForm(company.legal_form);
        setSiren(company.siren || "");
        setSiret(company.siret || "");
        setAddress(company.address || "");
        setCity(company.city || "");
        setPostalCode(company.postal_code || "");
        setActivityDescription(company.activity_description || "");
      } else {
        setName("");
        setLegalForm("auto_entrepreneur");
        setSiren("");
        setSiret("");
        setAddress("");
        setCity("");
        setPostalCode("");
        setActivityDescription("");
      }
    }
  }, [open, company]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSubmit({
      name: name.trim(),
      legal_form: legalForm,
      siren: siren.trim() || null,
      siret: siret.trim() || null,
      address: address.trim() || null,
      city: city.trim() || null,
      postal_code: postalCode.trim() || null,
      activity_description: activityDescription.trim() || null,
    });

    onOpenChange(false);
  };

  const isValid = name.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier l'entreprise" : "Ajouter une entreprise"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifiez les informations de cette entreprise"
              : "Ajoutez une entreprise pour gérer ses documents légaux"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom de l'entreprise *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ma Société SAS"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="legalForm">Forme juridique</Label>
            <Select
              value={legalForm}
              onValueChange={(v) => setLegalForm(v as CompanyLegalForm)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_FORMS.map((form) => (
                  <SelectItem key={form} value={form}>
                    <span className="flex items-center gap-2">
                      <span>{LEGAL_FORM_ICONS[form]}</span>
                      <span>{LEGAL_FORM_LABELS[form]}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="siren">SIREN</Label>
              <Input
                id="siren"
                value={siren}
                onChange={(e) => setSiren(e.target.value)}
                placeholder="123 456 789"
                maxLength={11}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="siret">SIRET</Label>
              <Input
                id="siret"
                value={siret}
                onChange={(e) => setSiret(e.target.value)}
                placeholder="123 456 789 00001"
                maxLength={17}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="12 rue de la Paix"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="postalCode">Code postal</Label>
              <Input
                id="postalCode"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="75001"
                maxLength={5}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Ville</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Paris"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="activity">Description de l'activité</Label>
            <Textarea
              id="activity"
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
              placeholder="Conseil en informatique, BTP, Commerce..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={!isValid || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? "Modification..." : "Ajout..."}
                </>
              ) : isEditing ? (
                "Modifier"
              ) : (
                "Ajouter"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
